import React, { useSyncExternalStore } from "react";
import { ThemesProvider } from "@gusarov-studio/rubik-ui";
import { usePreferences } from "../hooks/usePreferences";
import type { ColorThemePreference } from "../slices/preferencesSlice";

const THEMES = {
  light: "r-theme-light",
  dark: "r-theme-dark",
} as const;

function getSystemIsDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function subscribeToColorScheme(callback: () => void) {
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaQuery.addEventListener("change", callback);

  return () => {
    mediaQuery.removeEventListener("change", callback);
  };
}

function resolveTheme(preference: ColorThemePreference, systemIsDark: boolean): string {
  switch (preference) {
    case "light":
      return THEMES.light;
    case "dark":
      return THEMES.dark;
    case "system":
      return systemIsDark ? THEMES.dark : THEMES.light;
  }
}

const AppThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  "use memo";
  const [preferences] = usePreferences();
  const systemIsDark = useSyncExternalStore(subscribeToColorScheme, getSystemIsDark);
  const resolved = resolveTheme(preferences.colorTheme, systemIsDark);

  return <ThemesProvider theme={resolved}>{children}</ThemesProvider>;
};

export { AppThemeProvider };
